import moment from 'moment'

export const getRelationId = relation => {
	if (!relation) return ''
	if (typeof relation === 'object') return relation?._id || relation?.id || ''
	return relation
}

const looksLikeObjectId = value => /^[a-fA-F0-9]{24}$/.test(String(value))

export const getRelationValue = relation => {
	if (!relation) return '-'
	if (typeof relation === 'string') return looksLikeObjectId(relation) ? '-' : relation
	if (typeof relation !== 'object') return String(relation)

	return relation.nazwa || relation.typ || relation.imie_i_nazwisko || relation.alternative_name || relation.name || '-'
}

export const getFirstRelationValue = (...relations) => {
	for (const relation of relations) {
		const value = getRelationValue(relation)
		if (value !== '-') return value
	}

	return '-'
}

export const formatDateTime = value => {
	if (!value) return '-'
	return moment(value).isValid() ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
}

const pad = value => String(value).padStart(2, '0')

export const toDatetimeLocal = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const toDateLocal = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const toApiDate = value => {
	if (!value) return ''
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

export const getFeedbackText = rozmowa => {
	const feedback = rozmowa?.feedback
	if (typeof feedback === 'string') return feedback
	return ''
}

export const getFeedback = rozmowa => getFeedbackText(rozmowa) || '-'

export const getSzansa = rozmowa => {
	const value = rozmowa?.szansa
	return value === true || value === 1 || value === '1' || value === 'true'
}

export const getValue = value => {
	if (value === 0 || value === '0') return '0'
	return value || '-'
}

const getFieldValue = (source, fields) => {
	if (!source || typeof source !== 'object') return ''

	for (const field of fields) {
		const value = source?.[field]
		if (value === 0 || value === '0') return '0'
		if (typeof value === 'string' && value.trim()) return value.trim()
		if (value && typeof value !== 'string') return value
	}

	return ''
}

const getTextValue = value => {
	if (!value) return ''
	if (typeof value === 'string') return value.trim()
	if (typeof value !== 'object') return String(value)

	return getTextValue(
		value?.text ||
			value?.tekst ||
			value?.content ||
			value?.tresc ||
			value?.value ||
			value?.analiza ||
			value?.transkrypt ||
			value?.transcript ||
			value?.transkrypcja
	)
}

const getUrlValue = value => {
	if (!value) return ''
	if (typeof value === 'string') return value.trim()
	if (typeof value !== 'object') return String(value)

	return getUrlValue(
		value?.url ||
			value?.downloadUrl ||
			value?.href ||
			value?.src ||
			value?.path ||
			value?.file ||
			value?.fileUrl ||
			value?.audioUrl
	)
}

export const getTranscript = rozmowa =>
	getTextValue(
		getFieldValue(rozmowa, [
			'transkrypt',
			'transcript',
			'transcription',
			'transkrypcja',
			'transkrypcje',
			'call_transcript',
			'callTranscript',
			'stt',
			'tekst_transkrypcji',
			'tekstTranskrypcji',
		])
	)

export const getSummary = rozmowa =>
	getTextValue(
		getFieldValue(rozmowa, [
			'analiza',
			'summary',
			'podsumowanie',
			'streszczenie',
			'ai_summary',
			'aiSummary',
			'ai_podsumowanie',
			'aiPodsumowanie',
			'podsumowanie_ai',
			'podsumowanieAi',
			'call_summary',
			'callSummary',
			'conversation_summary',
			'conversationSummary',
			'rozmowa_summary',
			'rozmowaSummary',
		])
	)

export const getNextSteps = rozmowa =>
	getTextValue(
		getFieldValue(rozmowa, [
			'kroki',
			'next_steps',
			'nextSteps',
			'nastepne_kroki',
			'nastepneKroki',
		])
	)

export const getRecordingUrl = rozmowa =>
	getUrlValue(
		getFieldValue(rozmowa, [
			'recording_url',
			'recordingUrl',
			'recording',
			'nagranie',
			'nagranie_url',
			'nagranieUrl',
			'call_recording',
			'callRecording',
			'audio_url',
			'audioUrl',
			'recording_path',
			'recordingPath',
			'recording_file',
			'recordingFile',
			'recording_download_url',
			'recordingDownloadUrl',
		])
	)

export const getTwilioCallStatus = rozmowa =>
	getTextValue(
		getFieldValue(rozmowa, ['twilio_call_status', 'twilioCallStatus', 'call_status', 'callStatus'])
	)

export const getTwilioCallDuration = rozmowa => {
	const value = getFieldValue(rozmowa, [
		'twilio_call_duration',
		'twilioCallDuration',
		'call_duration',
		'callDuration',
	])

	if (value === '' || value === null || value === undefined) return null

	const seconds = typeof value === 'number' ? value : Number(String(value).trim())
	return Number.isFinite(seconds) && seconds >= 0 ? seconds : null
}

export const formatCallDuration = seconds => {
	if (seconds === null || seconds === undefined || seconds === '') return ''

	const totalSeconds = Math.floor(Number(seconds))
	if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return ''

	const hours = Math.floor(totalSeconds / 3600)
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const remainingSeconds = totalSeconds % 60

	if (hours > 0) {
		return `${hours}:${pad(minutes)}:${pad(remainingSeconds)}`
	}

	return `${minutes}:${pad(remainingSeconds)}`
}

const phoneFields = [
	'tel',
	'telefon',
	'phone',
	'mobile',
	'komorka',
	'numer_telefonu',
	'telefon_komorkowy',
	'phoneNumber',
	'customerTel',
	'kontakt',
	'telefon1',
	'telefon_1',
]

const getPhoneFromSource = source => {
	const phone = getTextValue(getFieldValue(source, phoneFields))
	if (phone) return phone

	const text = getTextValue(source?.imie_i_nazwisko || source?.nazwa || source?.name)
	const match = text.match(/(?:\+?\d[\d\s().-]{6,}\d)/)

	return match?.[0]?.trim() || ''
}

const normalizePhoneNumber = phoneNumber => {
	if (!phoneNumber) return ''

	const value = String(phoneNumber).trim()
	const digits = value.replace(/\D/g, '')

	if (!digits) return ''

	return value.startsWith('+') ? `+${digits}` : digits
}

export const getPhoneNumber = rozmowa =>
	normalizePhoneNumber(
		getPhoneFromSource(rozmowa) ||
			getPhoneFromSource(rozmowa?.id_osoba) ||
			getPhoneFromSource(rozmowa?.osoba) ||
			getPhoneFromSource(rozmowa?.id_placowka) ||
			getPhoneFromSource(rozmowa?.placowka)
	)

export const getOsobaPhoneNumber = rozmowa => {
	const osoba = rozmowa?.id_osoba || rozmowa?.osoba
	return normalizePhoneNumber(getPhoneFromSource(osoba))
}
